import { createRemoteAppComponent } from '@module-federation/bridge-react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Divider, Grid, IconButton, MenuItem, Paper, Stack, Typography } from '@mui/material';
import queryString from 'query-string';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_ADVERTISERS_SEARCH_URL, API_CLIENTS_SEARCH } from '../../constants/apiUrls';
import { contractFormText } from '../../constants/contracts';
import { ADVERTISERS_EDIT_URL, CLIENTS_EDIT_URL } from '../../constants/routeUrls';
import { useAuth } from '../../hooks/useAuth';
import { useConfig } from '../../hooks/useConfig';
import usePrevious from '../../hooks/usePrevious';
import loadUserManagementRemote from '../../util/remotes/userManagement';
import { getRequestJSON } from '../../util/services';
import ControlledTypeaheadTextField from '../forms/ControlledTypeaheadTextField';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import BreakingFlex from '../shared/BreakingFlex';
import CustomBillingOptionsList from './CustomBillingOptionsList';

const OrganizationModal = createRemoteAppComponent({
  loader: () => loadUserManagementRemote('CreateOrganizationModal-bridge'),
  loading: <div>Loading...</div>,
  fallback: ({ error }) => <div>Failed to load: {error.message}</div>,
});

const AdvertiserModal = createRemoteAppComponent({
  loader: () => loadUserManagementRemote('UpdateAdvertiserModal-bridge'),
  loading: <div>Loading...</div>,
  fallback: ({ error }) => <div>Failed to load: {error.message}</div>,
});

function ContractClientBillingSection() {
  const { watch, setValue, formState: { isSubmitSuccessful } } = useFormContext();
  const [isEditing, setIsEditing] = useState(false);
  const isSubmitSuccessfulPrevious = usePrevious(isSubmitSuccessful);

  useEffect(() => {
    if (isSubmitSuccessfulPrevious !== isSubmitSuccessful && isSubmitSuccessful) {
      setIsEditing(false);
    }
  }, [isSubmitSuccessful, isSubmitSuccessfulPrevious]);

  const isNewContract = watch('contractId') == null;
  const canEdit = watch('clientAdvEditable');

  const selectedClientId = watch('selectedClientId');
  const selectedSsoClientId = watch('selectedSsoClientId');
  const selectedAdvertiserId = watch('selectedAdvertiserId');
  const selectedClientName = watch('selectedClientName');
  const selectedAdvertiserName = watch('selectedAdvertiserName');

  const { apiHost } = useConfig();
  const authUser = useAuth();
  const navigate = useNavigate();

  const [newClientModal, setNewClientModal] = useState(false);
  const [newAdvertiserModal, setNewAdvertiserModal] = useState(false);

  const handleOpenNewClientModal = () => {
    setNewClientModal(true);
  };

  const handleCloseNewClientModal = () => {
    setNewClientModal(false);
  };

  const handleOpenNewAdvertiserModal = () => {
    setNewAdvertiserModal(true);
  };

  const handleCloseNewAdvertiserModal = () => {
    setNewAdvertiserModal(false);
  };

  const getPaperComponent = ({ children, onMouseDown, label }) => (
    <Paper>
      {children}
      <Divider />
      <MenuItem value="new" onMouseDown={() => onMouseDown()}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ borderTop: '1px' }}>
          <AddIcon color="primary" sx={{ width: '20px', height: '20px' }} />
          <Typography sx={{ width: 'auto' }} color="primary" width="100%" align="center">
            {label}
          </Typography>
        </Stack>
      </MenuItem>
    </Paper>
  );

  const getClientOptions = useCallback(async (term) => {
    const response = await getRequestJSON(
      apiHost,
      generatePath(API_CLIENTS_SEARCH, { '*': `?${queryString.stringify({ term })}` })
    );
    return response.map((item) => ({ label: item.label, value: Number(item.id), ssoId: Number(item.sso_id) }));
  }, [apiHost]);

  const getAdvertiserOptions = useCallback(async (term) => {
    if (!selectedClientId) return [];
    const response = await getRequestJSON(
      apiHost,
      generatePath(API_ADVERTISERS_SEARCH_URL, {
        '*': `?${queryString.stringify({ client_id: selectedClientId, term })}`,
      })
    );
    return response.map((item) => ({ label: item.label, value: Number(item.id) }));
  }, [apiHost, selectedClientId]);

  const handleClientSelect = (option) => {
    const newClientId = option?.value;
    const currentClientId = selectedClientId;
    
    setValue('selectedClientId', newClientId, { shouldDirty: true });
    setValue('selectedClientName', option?.label || '', { shouldDirty: true });
    setValue('selectedSsoClientId', option?.ssoId, { shouldDirty: true });

    // Only clear advertiser if client actually updated
    if (newClientId !== currentClientId) {
      setValue('selectedAdvertiserId', null, { shouldDirty: true });
      setValue('selectedAdvertiserName', '', { shouldDirty: true });
    }
  };

  const handleAdvertiserSelect = (option) => {
    setValue('selectedAdvertiserId', option?.value, { shouldDirty: true });
    setValue('selectedAdvertiserName', option?.label || '', { shouldDirty: true });
  };

  const currentClientOption = selectedClientId && selectedClientName 
    ? { label: selectedClientName, value: selectedClientId }
    : null;
    
  const currentAdvertiserOption = (selectedAdvertiserId !== null && selectedAdvertiserId !== undefined && selectedAdvertiserName)
    ? { label: selectedAdvertiserName, value: selectedAdvertiserId }
    : null;

  return (
    <FormSection title={contractFormText.sectionTitles.clientBilling}>
      <FormSubsection
        heading={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.primary">
              {contractFormText.subsectionTitles.clientAndAd}
            </Typography>
            {canEdit && !isEditing && (
              <IconButton size="small" onClick={() => setIsEditing(true)} color="success">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        }
      >
        <Grid container>
          <BreakingFlex breakpoint="md">
            <Grid item xs={3}>
              {canEdit && (isNewContract || isEditing) ? (
                <ControlledTypeaheadTextField
                  name="selectedClientId"
                  label="Client"
                  value={currentClientOption}
                  getOptions={getClientOptions}
                  onChange={handleClientSelect}
                  fullWidth
                  PaperComponent={({ children }) => getPaperComponent({ children, onMouseDown: handleOpenNewClientModal, label: 'New Client' })
                  }
                />
              ) : (
                <Stack spacing={1}>
                  <TextField
                    name="selectedClientName"
                    label="Client"
                    value={selectedClientName || ''}
                    disabled
                    fullWidth
                  />
                  {selectedClientId && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => navigate(generatePath(CLIENTS_EDIT_URL, { id: selectedClientId.toString() }))}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Visit Client Page
                    </Button>
                  )}
                </Stack>
              )}
              <Suspense fallback={<div>Loading...</div>}>
                {newClientModal && (
                  <OrganizationModal
                    accessToken={authUser.user.sso_token}
                    canCreateOrganizations
                    open={newClientModal}
                    closeDialog={() => handleCloseNewClientModal()}
                  />
                )}
              </Suspense>
            </Grid>
            <Grid item xs={3}>
              {canEdit && (isNewContract || isEditing) ? (
                <ControlledTypeaheadTextField
                  key={`advertiser-${selectedClientId}`}
                  name="selectedAdvertiserId"
                  label="Advertiser"
                  value={currentAdvertiserOption}
                  disabled={!selectedClientId}
                  getOptions={getAdvertiserOptions}
                  onChange={handleAdvertiserSelect}
                  fullWidth
                  PaperComponent={
                      ({ children }) => getPaperComponent({ children, onMouseDown: handleOpenNewAdvertiserModal, label: 'New Advertiser' })
                  }
                />
              ) : (
                <Stack spacing={1}>
                  <TextField
                    name="selectedAdvertiserName"
                    label="Advertiser"
                    value={selectedAdvertiserName || ''}
                    disabled
                    fullWidth
                  />
                  {selectedAdvertiserId && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => navigate(generatePath(ADVERTISERS_EDIT_URL, { advertiserId: selectedAdvertiserId.toString() }))}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Visit Advertiser Page
                    </Button>
                  )}
                </Stack>
              )}
              <Suspense fallback={<div>Loading...</div>}>
                {newAdvertiserModal && (
                  <AdvertiserModal
                    accessToken={authUser.user.sso_token}
                    canCreateAdvertisers
                    open={newAdvertiserModal}
                    organizationId={selectedSsoClientId}
                    closeDialog={() => handleCloseNewAdvertiserModal()}
                  />
                )}
              </Suspense>
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Domain"
                name="domain"
                fullWidth
                autoComplete="on"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField label="Salesforce ID (Optional)" name="salesforceId" fullWidth />
            </Grid>
          </BreakingFlex>
        </Grid>
      </FormSubsection>
      <FormSubsection title={contractFormText.subsectionTitles.billing}>
        <Grid container>
          <BreakingFlex breakpoint="md">
            <Grid item xs={3}>
              {/* Billing model input currently only for display */}
              <Select
                name="billingModel"
                disabled
                label="Billing Model"
                fullWidth
                options={[{ value: 'managedservice', label: 'Managed Service' }]}
              />
            </Grid>
            <Grid item xs={3}>
              <Select
                name="billingOption"
                label="Billing Option"
                fullWidth
                options={[
                  { value: '1ST_PARTY_BILLING', label: '1st Party Billing' },
                  { value: '3RD_PARTY_BILLING_DCM', label: '3rd Party Billing - DCM' },
                  { value: '3RD_PARTY_BILLING_OTHER', label: '3rd Party Billing - Other' },
                  { value: 'MANUAL_BILLING', label: 'Manual Billing' },
                  { value: 'PREPAID_BILLING', label: 'Prepaid Billing' },
                  { value: '3RD_PARTY_PREPAID_BILLING', label: 'Prepaid Billing - 3rd Party' },
                  { value: 'PREPAID_OTHER', label: 'Prepaid Billing - Other' },
                ]}
              />
            </Grid>
            <Grid item xs={3}>
              <Select
                name="billingTerms"
                label="Billing Terms"
                fullWidth
                options={[
                  { value: 'net30', label: 'Net 30' },
                  { value: 'net45', label: 'Net 45' },
                  { value: 'net60', label: 'Net 60' },
                  { value: 'net75', label: 'Net 75' },
                  { value: 'net90', label: 'Net 90' },
                  { value: 'net120', label: 'Net 120' },
                ]}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Billing Email Address"
                name="billingEmails"
                autoComplete="on"
              />
            </Grid>
          </BreakingFlex>
        </Grid>
      </FormSubsection>
      <CustomBillingOptionsList />
    </FormSection>
  );
}

export default ContractClientBillingSection;